import axios from 'axios';
import { ProcessedBetSlipData, BetSlip } from '../../../backend/src/models/BetSlip';
import { User } from '../../../backend/src/models/User';
import { RegisterData, TicketIdCheckResult } from '../types/auth';
import { getApiBaseUrl } from '../utils/urlUtils';
import { ExportColumns, ExportRequest } from '../types/betslip';

// Create API instance with base URL (already includes /api)
const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

// Auth service
export const authService = {
  async register(data: RegisterData): Promise<User> {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  async getUser(): Promise<User | null> {
    try {
      const response = await api.get('/auth/user');
      return response.data;
    } catch (error) {
      return null;
    }
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout');
  }
};

// Type for API responses that include full bet slip data
type BetSlipResponse = ProcessedBetSlipData & Pick<BetSlip, 'id' | 'status' | 'notes'>;

// Bet slip endpoints
export async function processImage(file: File): Promise<ProcessedBetSlipData> {
  const formData = new FormData();
  formData.append('image', file);

  const response = await api.post('/bet-slips/process-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

export async function saveBetSlip(data: ProcessedBetSlipData): Promise<BetSlipResponse> {
  const response = await api.post('/bet-slips', {
    ...data,
  });

  return response.data;
}

export async function getUserBetSlips(userId: number): Promise<BetSlipResponse[]> {
  const response = await api.get(`/bet-slips/user/${userId}`);
  return response.data;
}

export async function getBetSlip(id: number): Promise<BetSlipResponse> {
  const response = await api.get(`/bet-slips/${id}`);
  return response.data;
}

export async function updateBetSlip(
  id: number,
  data: Partial<BetSlip>,
  image?: File
): Promise<BetSlipResponse> {
  const formData = new FormData();
  
  // Append image if provided
  if (image) {
    formData.append('image', image);
  }

  // Convert data to JSON string and append to form data
  formData.append('data', JSON.stringify({
    casinoName: data.casinoName,
    notes: data.notes,
    status: data.status,
    settledDateTime: data.settledDateTime,
    eventDateTime: data.eventDateTime,
  }));

  // Log the data being sent
  console.log('Sending update data:', {
    id,
    data: JSON.stringify({
      casinoName: data.casinoName,
      notes: data.notes,
      status: data.status,
      settledDateTime: data.settledDateTime,
      eventDateTime: data.eventDateTime,
    }),
    hasImage: !!image
  });

  const response = await api.put(`/bet-slips/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

export type BetSlipStatus = 'pending' | 'won' | 'lost' | 'push' | 'cancelled';

export async function updateBetSlipStatus(
  id: number,
  status: BetSlipStatus
): Promise<BetSlipResponse> {
  const response = await api.patch(`/bet-slips/${id}/status`, { status });
  return response.data;
}

export async function deleteBetSlip(id: number): Promise<void> {
  await api.delete(`/bet-slips/${id}`);
}

export async function getExportColumns(): Promise<ExportColumns> {
  try {
    console.log('Fetching export columns...');
    const response = await api.get('/bet-slips/export/columns');
    console.log('Received columns:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching export columns:', error);
    throw error;
  }
}

export async function exportToExcel(request: ExportRequest): Promise<void> {
  try {
    // Debug logging for request
    console.log('Excel export request details:', {
      hasFilters: !!request.filters,
      filterValues: request.filters && {
        startDate: request.filters.startDate?.toISOString(),
        endDate: request.filters.endDate?.toISOString(),
        casino: request.filters.casino,
        status: request.filters.status,
        betType: request.filters.betType
      },
      hasSelectedColumns: !!request.selectedColumns,
      columnCounts: request.selectedColumns && {
        betSlips: request.selectedColumns.betSlips.length,
        selections: request.selectedColumns.selections.length
      },
      betSlipColumns: request.selectedColumns?.betSlips,
      selectionColumns: request.selectedColumns?.selections
    });

    const response = await api.post('/bet-slips/export/excel', request, {
      responseType: 'blob'
    });

    // Debug logging for response
    console.log('Excel export response details:', {
      status: response.status,
      statusText: response.statusText,
      headers: {
        contentType: response.headers['content-type'],
        contentLength: response.headers['content-length'],
        contentDisposition: response.headers['content-disposition']
      },
      data: {
        type: response.data?.type,
        size: response.data?.size,
        isBlob: response.data instanceof Blob
      }
    });

    // Create blob
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    // Debug logging for blob
    console.log('Excel blob details:', {
      type: blob.type,
      size: blob.size,
      isBlob: blob instanceof Blob
    });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = response.headers['content-disposition']?.split('filename=')[1].replace(/"/g, '') || 'betfolio_export.xlsx';
    link.setAttribute('download', filename);

    // Debug logging for download
    console.log('Excel download details:', {
      url: url.substring(0, 50) + '...',
      filename,
      blobUrl: url.startsWith('blob:')
    });

    // Trigger download
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    console.log('Excel export completed');
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      console.error('Excel export error:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        isAxiosError: true,
        response: error.response && {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        }
      });
    } else {
      console.error('Excel export error:', error);
    }
    throw error;
  }
}

export async function exportToPDF(request: ExportRequest): Promise<void> {
  const response = await api.post('/bet-slips/export/pdf', request, {
    responseType: 'blob'
  });
  
  // Create a blob from the response data
  const blob = new Blob([response.data], {
    type: 'application/pdf'
  });
  
  // Create a link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', response.headers['content-disposition']?.split('filename=')[1].replace(/"/g, '') || 'betfolio_export.pdf');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function checkTicketIdExists(ticketId: string): Promise<TicketIdCheckResult> {
  try {
    const response = await api.get(`/bet-slips/check-ticket/${ticketId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return {
        exists: true,
        betSlipId: error.response.data.existingBetSlipId
      };
    }
    return { exists: false };
  }
}